/* PACKLAB VacPack v2 product page: gallery, variant sync, quantity, sticky bar, countdown.
   No dependencies. Works with Dawn's <product-form> (product-form.js) for add to cart. */
(() => {
  if (window.plVacpackLoaded) return;
  window.plVacpackLoaded = true;

  const qsa = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Gallery ---------- */
  function initGallery(root) {
    const track = root.querySelector('[data-pl-track]');
    if (!track || track.dataset.plReady) return;
    track.dataset.plReady = 'true';
    const slides = Array.from(track.children);
    const thumbs = qsa('[data-pl-thumb]', root);
    let current = 0;

    const goTo = (index, smooth = true) => {
      if (!slides.length) return;
      index = Math.max(0, Math.min(slides.length - 1, index));
      track.scrollTo({ left: slides[index].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
      setCurrent(index);
    };

    const setCurrent = (index) => {
      current = index;
      thumbs.forEach((t, i) => {
        if (i === index) {
          t.setAttribute('aria-current', 'true');
        } else {
          t.removeAttribute('aria-current');
        }
      });
    };

    let ticking = false;
    track.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          setCurrent(Math.round(track.scrollLeft / track.clientWidth));
          ticking = false;
        });
      },
      { passive: true }
    );

    thumbs.forEach((t) => t.addEventListener('click', () => goTo(Number(t.dataset.plThumb))));
    root.querySelector('[data-pl-prev]')?.addEventListener('click', () => goTo(current - 1));
    root.querySelector('[data-pl-next]')?.addEventListener('click', () => goTo(current + 1));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goTo(current - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goTo(current + 1);
      }
    });

    root.plGoToMedia = (mediaId) => {
      const index = slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (index > -1) goTo(index);
    };
  }

  /* ---------- Variant sync across every PL form on the page ---------- */
  function applyVariant(variant) {
    qsa('[data-pl-variant-input]').forEach((input) => {
      input.value = variant.id;
      input.disabled = !variant.available;
    });
    qsa('[data-pl-atc-button]').forEach((btn) => {
      btn.disabled = !variant.available;
      const label = btn.querySelector('[data-pl-atc-label]');
      if (label) label.textContent = variant.available ? label.dataset.label : label.dataset.soldOut;
    });
    qsa('[data-pl-price]').forEach((el) => (el.textContent = variant.price));
    qsa('[data-pl-compare]').forEach((el) => {
      el.textContent = variant.compare;
      el.hidden = !variant.compare;
    });
    qsa('[data-pl-save]').forEach((el) => {
      el.textContent = (el.dataset.label || '').replace('[percent]', variant.save);
      el.hidden = !variant.save;
    });
    qsa('[data-pl-variant-title]').forEach((el) => (el.textContent = variant.options.join(' / ')));

    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      /* ignore */
    }
  }

  function initBuybox(root) {
    if (root.dataset.plReady) return;
    root.dataset.plReady = 'true';
    const gallery = root.querySelector('[data-pl-gallery]');
    if (gallery) initGallery(gallery);

    const jsonEl = root.querySelector('[data-pl-product-json]');
    if (!jsonEl) return;
    let data;
    try {
      data = JSON.parse(jsonEl.textContent);
    } catch (e) {
      return;
    }

    const fieldsets = qsa('[data-pl-option]', root);
    fieldsets.forEach((fs) =>
      fs.addEventListener('change', () => {
        const selected = fieldsets.map((f) => f.querySelector('input:checked')?.value);
        fieldsets.forEach((f, i) => {
          const label = f.querySelector('[data-pl-selected]');
          if (label) label.textContent = selected[i] || '';
        });
        const variant = data.variants.find((v) => v.options.every((o, i) => o === selected[i]));
        if (!variant) return;
        applyVariant(variant);
        if (variant.mediaId && gallery && gallery.plGoToMedia) gallery.plGoToMedia(variant.mediaId);
      })
    );

    // Start the gallery on the selected variant's image when the URL has ?variant=
    if (data.initialMediaId && gallery && gallery.plGoToMedia && window.location.search.includes('variant=')) {
      requestAnimationFrame(() => gallery.plGoToMedia(data.initialMediaId));
    }

    qsa('[data-pl-qty]', root).forEach((btn) =>
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('input');
        const next = Math.max(Number(input.min) || 1, (parseInt(input.value, 10) || 1) + Number(btn.dataset.plQty));
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      })
    );
  }

  /* ---------- Sticky add to cart ---------- */
  // Uses a rAF-throttled scroll check rather than an IntersectionObserver, because bundle apps
  // can re-render the buy-box form and leave an observer watching a detached button.
  let stickyBound = false;
  function initSticky() {
    if (stickyBound) return updateSticky();
    stickyBound = true;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateSticky();
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    updateSticky();
  }

  function updateSticky() {
    const sticky = document.querySelector('[data-pl-sticky]');
    const mainButton = document.querySelector('[data-pl-main-atc]');
    if (!sticky) return;
    // Show only once the main button has scrolled up out of view (not before reaching it),
    // and step aside if a bundle app is already showing its own sticky bar.
    const appSticky =
      sticky.dataset.deferToApp === 'true' &&
      document.querySelector('kaching-bundles-sticky-atc, [class*="sticky-atc"]:not(.pl-sticky)');
    const appStickyShown = !!appSticky && appSticky.getClientRects().length > 0;
    const visible = !appStickyShown && !!mainButton && mainButton.getBoundingClientRect().bottom < 0;
    if (sticky.classList.contains('is-visible') === visible) return;
    sticky.classList.toggle('is-visible', visible);
    sticky.setAttribute('aria-hidden', String(!visible));
    sticky.toggleAttribute('inert', !visible);
  }

  /* ---------- Countdown (only with a real end date) ---------- */
  function initCountdowns(root = document) {
    qsa('[data-pl-countdown]', root).forEach((el) => {
      if (el.dataset.plReady) return;
      el.dataset.plReady = 'true';
      const end = Date.parse(el.dataset.end);
      const out = el.querySelector('[data-pl-countdown-time]');
      if (Number.isNaN(end) || end <= Date.now()) return;
      const pad = (n) => String(n).padStart(2, '0');
      const tick = () => {
        const left = end - Date.now();
        if (left <= 0) {
          el.hidden = true;
          clearInterval(timer);
          return;
        }
        const s = Math.floor(left / 1000);
        const d = Math.floor(s / 86400);
        const h = Math.floor((s % 86400) / 3600);
        const m = Math.floor((s % 3600) / 60);
        out.textContent = `${d > 0 ? d + 'd ' : ''}${pad(h)}:${pad(m)}:${pad(s % 60)}`;
      };
      const timer = setInterval(tick, 1000);
      tick();
      el.hidden = false;
    });
  }

  /* ---------- Secondary buttons (sticky bar, final CTA) submit through the main buy box ----------
     So bundle / upsell apps attached to the main form (and its quantity) apply to every add-to-cart. */
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form.closest || !form.closest('[data-pl-proxy]')) return;
      const main = document.querySelector('[data-pl-main-atc]');
      if (!main || main.disabled) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      main.click();
    },
    true
  );

  function init(root = document) {
    qsa('[data-pl-buybox]', root).forEach(initBuybox);
    if (root.matches && root.matches('[data-pl-buybox]')) initBuybox(root);
    initCountdowns(root);
    initSticky();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
